/*
 *  -----------------------------------------------------------------------
 *    <copyright file="add-function-dialog.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import React, { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { orderBy } from '@progress/kendo-data-query';
import { Checkbox } from '@progress/kendo-react-inputs';

import { restructuredUpdatedCreatedBy } from '@utils-clean/update-users-array';
import { useBoolean, useTranslation } from 'hooks';
import { GET_NAMED_ITEMS_BY_TYPE, SAVE_NAMED_ITEM } from 'queries/common';
import { StudyFields, NamedItemFields } from 'queries/generated/fragments.generated';
import { ASSIGN_FUNCTIONS, GET_STUDY } from 'queries/studies';

import 'views/components/admin/studies/function-dialogs/styles.scss';
import { NamedItemType } from 'types/graphql-global-types';
import NotificationService from 'utils/notification';
import { checkIfNameExist, validateFields } from 'utils/validation';
import { SaveButton } from 'views/components/core/buttons';
import CharactersRemaining from 'views/components/core/characters-remaining';
import { CustomDialog, DialogTitle, DialogContent, DialogActions } from 'views/components/core/dialog';
import TextField from 'views/components/core/text-field';
import Constants from '@utils/constants';
import { getNamedItemsByType, getNamedItemsByTypeVariables } from '@queries/generated/common.generated';

interface AddFunctionDialogProps {
    study: StudyFields;
    open: boolean;
    onClose: Function;
}

const AddFunctionDialog = ({ study, open, onClose }: AddFunctionDialogProps) => {
    const { t } = useTranslation();
    const [name, setName] = useState('');
    const [nameError, setNameError] = useState('');
    const active = useBoolean(false);
    const disabledSave = useBoolean(true);
    const [saveNamedItem] = useMutation(SAVE_NAMED_ITEM);
    const [assignFunctions] = useMutation(ASSIGN_FUNCTIONS);
    const [studyFunctionsData, setStudyFunctionsData] = useState<NamedItemFields[]>([]);
    const studyFunctionRequest = useQuery<getNamedItemsByType, getNamedItemsByTypeVariables>(GET_NAMED_ITEMS_BY_TYPE, {
        variables: {
            type: NamedItemType.RESPONSIBLE_FUNCTION,
        },
    });
    const maxLength = 70;

    useEffect(() => {
        if (studyFunctionRequest.data?.namedItemsByType) {
            const studyFunctions =
                studyFunctionRequest.data.namedItemsByType
                    .filter(
                        (studyFunction: any) =>
                            study.studyFunctionIds?.includes(studyFunction.id) || studyFunction.studyId === study.id,
                    )
                    .map((fun: any) => {
                        const _organizationFunction = fun.studyId === Constants.ObjectId.Empty;
                        return {
                            ...fun,
                            _organizationFunction,
                            created: _organizationFunction ? '' : fun.created,
                            createdBy: _organizationFunction ? null : fun.createdBy,
                        };
                    }) || [];
            const newData = restructuredUpdatedCreatedBy(studyFunctions);
            setStudyFunctionsData(
                orderBy(newData, [
                    { field: 'active', dir: 'desc' },
                    { field: 'name', dir: 'asc' },
                ]),
            );
        }
    }, [studyFunctionRequest, study]);

    const close = (action?: boolean) => {
        disabledSave.setTrue();
        setName('');
        setNameError('');
        onClose(action);
        active.setFalse();
    };

    const validate = (value: string) => {
        const invalidName = validateFields(value, 'name', maxLength, false);
        if (invalidName) {
            setNameError(invalidName.name);
        }
    };

    const onChangeName = (e: any) => {
        disabledSave.setFalse();
        setName(e.target.value);
        setNameError('');
        validate(e.target.value);
    };

    const errorRequest = () => {
        NotificationService.showNotification(t('errorMessage'));
        disabledSave.setFalse();
    };

    const successRequest = () => {
        NotificationService.showNotification(t('admin:functionSuccessfullyCreatedAndAssigned', { id: study.recordId }));
        close();
    };

    const onSubmit = () => {
        const validateError = validateFields(name, 'name', maxLength, true);
        if (validateError && validateError.name) {
            setNameError(validateError.name);
            return;
        }
        if (checkIfNameExist({ name, id: null }, studyFunctionsData, 'name')) {
            setNameError(t('admin:functionNameAlreadyExists'));
            return;
        }
        disabledSave.setTrue();
        saveNamedItem({
            variables: {
                id: null,
                type: NamedItemType.RESPONSIBLE_FUNCTION,
                name,
                active: active.value,
                studyId: study.id,
            },
        })
            .then((res) => {
                if (res.data) {
                    const studyFunctionIds = [...study.studyFunctionIds, res.data.saveNamedItem.id];

                    assignFunctions({
                        variables: { studyId: study.id, reason: '', studyFunctionIds },
                        refetchQueries: [
                            {
                                query: GET_STUDY,
                                variables: { id: study.id },
                            },
                        ],
                    }).then(() => {
                        successRequest();
                    });
                }
            })
            .catch(() => {
                errorRequest();
            });
    };

    return (
        <CustomDialog
            onClose={() => close()}
            aria-labelledby="add-function"
            fullWidth
            scroll="paper"
            maxWidth="md"
            open={open}
            className="custom-width-popup"
        >
            <DialogTitle id="add-function">
                {t('admin:addFunction')}
                <FontAwesomeIcon
                    icon={['fal', 'times']}
                    className="fa-lg close-icon-cv"
                    onClick={() => close()}
                    data-testid="close-dialog"
                />
            </DialogTitle>

            <DialogContent className="add-function-dialog-content">
                <Checkbox
                    rounded={null}
                    className="cell-checkbox no-full-width flex-start no-border"
                    checked={active.value}
                    onChange={() => {
                        disabledSave.setFalse();
                        active.setValue(!active.value);
                    }}
                    label={t('active')}
                    data-testid="active-checkbox"
                    size="medium"
                />
                <TextField
                    id="add-function-statement"
                    placeholder={t('admin:enterName')}
                    autoFocus
                    fullWidth
                    label={`${t('admin:function')}*`}
                    InputProps={{ labelWidth: 95 }}
                    value={name}
                    helperText={
                        <CharactersRemaining
                            id="add-function-dialog-statement"
                            length={name.length}
                            max={maxLength}
                            error={nameError}
                        />
                    }
                    onChange={onChangeName}
                    margin="normal"
                    variant="outlined"
                    error={!!nameError}
                />
            </DialogContent>

            <DialogActions className="actions-info-wrapper-add-function dialog-actions flex-end">
                <SaveButton
                    style={{ minWidth: 0 }}
                    id="add-function-save"
                    disabled={disabledSave.value || !!nameError}
                    text="createAssign"
                    onClick={onSubmit}
                />
            </DialogActions>
        </CustomDialog>
    );
};

export default AddFunctionDialog;
